import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authApi, User, LoginCredentials } from '@/api/auth';
import { tokenStorage, ImpersonatorInfo } from '@/lib/token';
import { toggleFavoriteSchool } from '@/api/schools';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  /** Check if user has a specific permission */
  hasPermission: (permission: string) => boolean;
  /** Check if user has any of the specified permissions */
  hasAnyPermission: (permissions: string[]) => boolean;
  /** All permissions from user's role */
  permissions: string[];
  /** Legacy: Check if user has a specific role code */
  hasRole: (role: string) => boolean;
  /** Legacy: Check if user has any of the specified role codes */
  hasAnyRole: (roles: string[]) => boolean;
  /** Impersonation: whether currently impersonating another user */
  isImpersonating: boolean;
  /** Impersonation: info about the original user (impersonator) */
  impersonator: ImpersonatorInfo | null;
  /** Impersonate another user (Super Admin only) */
  impersonate: (userId: number) => Promise<void>;
  /** Exit impersonation and return to original user */
  exitImpersonation: () => Promise<void>;
  /** Favorite school IDs for current user */
  favoriteSchoolIds: number[];
  /** Toggle a school as favorite, returns new favorite state */
  toggleFavorite: (code: string) => Promise<boolean>;
  /** Check if a school is a favorite */
  isFavorite: (schoolId: number) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonator, setImpersonator] = useState<ImpersonatorInfo | null>(null);
  const [favoriteSchoolIds, setFavoriteSchoolIds] = useState<number[]>([]);

  const fetchUser = useCallback(async () => {
    const token = tokenStorage.getAccessToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    if (tokenStorage.isTokenExpired(token)) {
      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) {
        tokenStorage.clearTokens();
        setIsLoading(false);
        return;
      }

      try {
        await authApi.refreshToken();
      } catch {
        tokenStorage.clearTokens();
        setIsLoading(false);
        return;
      }
    }

    try {
      const userData = await authApi.getCurrentUser();
      setUser(userData);
      setFavoriteSchoolIds(userData.favoriteSchoolIds || []);

      // Check impersonation status
      const impersonating = authApi.isImpersonating();
      setIsImpersonating(impersonating);
      if (impersonating) {
        setImpersonator(authApi.getImpersonator());
      } else {
        setImpersonator(null);
      }
    } catch {
      tokenStorage.clearTokens();
      setUser(null);
      setIsImpersonating(false);
      setImpersonator(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (credentials: LoginCredentials) => {
    await authApi.login(credentials);
    const userData = await authApi.getCurrentUser();
    setUser(userData);
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setIsImpersonating(false);
      setImpersonator(null);
    }
  };

  const impersonate = async (userId: number) => {
    await authApi.impersonate(userId);
    // Fetch the new user data after impersonation
    const userData = await authApi.getCurrentUser();
    setUser(userData);
    setIsImpersonating(true);
    setImpersonator(authApi.getImpersonator());
  };

  const exitImpersonation = async () => {
    const restored = await authApi.exitImpersonation();
    if (restored) {
      // Fetch the original user data
      const userData = await authApi.getCurrentUser();
      setUser(userData);
      setIsImpersonating(false);
      setImpersonator(null);
    }
  };

  // Get permissions from user's role
  const permissions = user?.permissions || [];

  // Permission-based access control
  const hasPermission = useCallback((permission: string): boolean => {
    return permissions.includes(permission);
  }, [permissions]);

  const hasAnyPermission = useCallback((permissionsToCheck: string[]): boolean => {
    return permissionsToCheck.some((p) => permissions.includes(p));
  }, [permissions]);

  // Favorites
  const toggleFavorite = useCallback(async (code: string): Promise<boolean> => {
    const result = await toggleFavoriteSchool(code);
    setFavoriteSchoolIds(result.favoriteSchoolIds);
    return result.isFavorite;
  }, []);

  const isFavorite = useCallback((schoolId: number): boolean => {
    return favoriteSchoolIds.includes(schoolId);
  }, [favoriteSchoolIds]);

  // Legacy role-based checks (kept for backward compatibility)
  const hasRole = useCallback((role: string): boolean => {
    return user?.roles?.includes(role) || false;
  }, [user?.roles]);

  const hasAnyRole = useCallback((roles: string[]): boolean => {
    return roles.some((role) => user?.roles?.includes(role));
  }, [user?.roles]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        hasPermission,
        hasAnyPermission,
        permissions,
        hasRole,
        hasAnyRole,
        isImpersonating,
        impersonator,
        impersonate,
        exitImpersonation,
        favoriteSchoolIds,
        toggleFavorite,
        isFavorite,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
